import type { DefinitionsFromApi, OverrideResultType } from '@reduxjs/toolkit/query'

import { type GetConfigurationByUuidApiArg, tmsApiService } from '../tmsApiService'
import type { EnhancedLegacyManCodeResponse, EnhancedTg3ToMaicConversionResponse } from './enhancedTg3Payload'
import type {
  EnhancedCavaApiResponse,
  EnhancedCavaByUuidApiArg,
  EnhancedConfigTypeInfo,
  EnhancedConfiguration,
  EnhancedConfigurationId,
  EnhancedConfigurationStepData,
  EnhancedGetAdmintoolMarketsByMarketIdReferenceTrucksAndReferenceTruckIdApiResponse,
  EnhancedGetAdmintoolMarketsByMarketIdReferenceTrucksApiResponse,
  EnhancedGetProductdataGetviewheadersByMarketIdApiArg,
  EnhancedGetProductdataMarketByMarketIdLanguagesApiArg,
  EnhancedManCode,
  EnhancedMarketInfo,
  EnhancedMarketLanguages,
  EnhancedPostAdmintoolMarketsByMarketIdReferenceTrucksApiResponse,
  EnhancedPostConfigurationByUuidDeselectVariantsApiArg,
  EnhancedPostConfigurationByUuidSelectVariantAndVariantIdApiArg,
  EnhancedPostConfigurationByUuidSelectVariantsApiArg,
  EnhancedPostConfigurationByUuidSelectVariantsApiResponse,
  EnhancedPostConfigurationByUuidStepAndStepApiArg,
  EnhancedPostConfigurationByUuidUndocommitApiArg,
  EnhancedPutAdmintoolMarketsByMarketIdReferenceTrucksAndReferenceTruckIdApiResponse,
  EnhancedPutConfigurationByUuidChangeGroupOfInterestApiArg,
  EnhancedVehicleTypeInfo,
  EnhancedViewHeaderInfo,
} from './enhancedTmsApiTypes.ts'
import type { OverrideArgumentResultType, UpdateApi, UpdateDefinitions, UpdateRecord } from './utils'

// type TagTypes = TagTypesFromApi<typeof apiService>
type Definitions = DefinitionsFromApi<typeof tmsApiService>

/**
 * These declarations may be incorrect
 * I've tried to enforce as much as I could through types to keep the structure
 */
type FinalDefinitions = {
  postConfiguration: OverrideResultType<Definitions['postConfiguration'], EnhancedConfiguration>
  getConfigurationByUuid: OverrideArgumentResultType<
    Definitions['getConfigurationByUuid'],
    UpdateRecord<GetConfigurationByUuidApiArg, { uuid: EnhancedConfigurationId }>,
    EnhancedConfigurationStepData
  >
  postConfigurationByUuidStepAndStep: OverrideArgumentResultType<
    Definitions['postConfigurationByUuidStepAndStep'],
    EnhancedPostConfigurationByUuidStepAndStepApiArg,
    EnhancedConfigurationStepData
  >
  postConfigurationByUuidSelectVariants: OverrideArgumentResultType<
    Definitions['postConfigurationByUuidSelectVariants'],
    EnhancedPostConfigurationByUuidSelectVariantsApiArg,
    EnhancedPostConfigurationByUuidSelectVariantsApiResponse
  >
  postConfigurationByUuidSelectVariantAndVariantId: OverrideArgumentResultType<
    Definitions['postConfigurationByUuidSelectVariantAndVariantId'],
    EnhancedPostConfigurationByUuidSelectVariantAndVariantIdApiArg,
    EnhancedConfigurationStepData
  >
  postConfigurationByUuidDeselectVariants: OverrideArgumentResultType<
    Definitions['postConfigurationByUuidDeselectVariants'],
    EnhancedPostConfigurationByUuidDeselectVariantsApiArg,
    EnhancedConfigurationStepData
  >
  postConfigurationByUuidUndocommit: OverrideArgumentResultType<
    Definitions['postConfigurationByUuidUndocommit'],
    EnhancedPostConfigurationByUuidUndocommitApiArg,
    EnhancedConfigurationStepData
  >
  getProductdataGetvehicletypeids: OverrideResultType<Definitions['getProductdataGetvehicletypeids'], EnhancedVehicleTypeInfo>
  getProductdataGetconfigurationtypeids: OverrideResultType<Definitions['getProductdataGetconfigurationtypeids'], EnhancedConfigTypeInfo>
  getProductdataGetviewheadersByMarketId: OverrideArgumentResultType<
    Definitions['getProductdataGetviewheadersByMarketId'],
    EnhancedGetProductdataGetviewheadersByMarketIdApiArg,
    EnhancedViewHeaderInfo
  >
  getProductdataMarketByMarketIdLanguages: OverrideArgumentResultType<
    Definitions['getProductdataMarketByMarketIdLanguages'],
    EnhancedGetProductdataMarketByMarketIdLanguagesApiArg,
    EnhancedMarketLanguages
  >
  getUserdataGetmarkets: OverrideResultType<Definitions['getUserdataGetmarkets'], EnhancedMarketInfo>
  putConfigurationByUuidChangeGroupOfInterest: OverrideArgumentResultType<
    Definitions['putConfigurationByUuidChangeGroupOfInterest'],
    EnhancedPutConfigurationByUuidChangeGroupOfInterestApiArg,
    EnhancedConfigurationStepData
  >
  postConvertEcmConfigByUuid: OverrideResultType<Definitions['postConvertEcmConfigByUuid'], EnhancedManCode>
  postCodeByIdCopy: OverrideResultType<Definitions['postCodeByIdCopy'], EnhancedManCode>
  getCodeById: OverrideResultType<Definitions['getCodeById'], { code: EnhancedManCode }>

  postCava: OverrideResultType<Definitions['postCava'], EnhancedCavaApiResponse>
  postCavaByUuid: OverrideArgumentResultType<Definitions['postCavaByUuid'], EnhancedCavaByUuidApiArg, EnhancedCavaApiResponse>

  postConvertTg3CodeByLanguage: OverrideResultType<Definitions['postConvertTg3CodeByLanguage'], EnhancedTg3ToMaicConversionResponse>
  getTg3CodeById: OverrideResultType<Definitions['getTg3CodeById'], EnhancedLegacyManCodeResponse>

  getAdmintoolMarketsByMarketIdReferenceTrucks: OverrideResultType<
    Definitions['getAdmintoolMarketsByMarketIdReferenceTrucks'],
    EnhancedGetAdmintoolMarketsByMarketIdReferenceTrucksApiResponse
  >
  postAdmintoolMarketsByMarketIdReferenceTrucks: OverrideResultType<
    Definitions['postAdmintoolMarketsByMarketIdReferenceTrucks'],
    EnhancedPostAdmintoolMarketsByMarketIdReferenceTrucksApiResponse
  >
  getAdmintoolMarketsByMarketIdReferenceTrucksAndReferenceTruckId: OverrideResultType<
    Definitions['getAdmintoolMarketsByMarketIdReferenceTrucksAndReferenceTruckId'],
    EnhancedGetAdmintoolMarketsByMarketIdReferenceTrucksAndReferenceTruckIdApiResponse
  >
  putAdmintoolMarketsByMarketIdReferenceTrucksAndReferenceTruckId: OverrideResultType<
    Definitions['putAdmintoolMarketsByMarketIdReferenceTrucksAndReferenceTruckId'],
    EnhancedPutAdmintoolMarketsByMarketIdReferenceTrucksAndReferenceTruckIdApiResponse
  >
}

export const enhancedTmsApiService = tmsApiService as unknown as UpdateApi<typeof tmsApiService, UpdateDefinitions<Definitions, FinalDefinitions>>
